'use client';

import type { UriTemplate } from '@/app/utils/request/api-routes';
import type { CommentEntityType } from '~/components/Interaction/Thread/model';
import type { AnswerType } from '~/model/AnswerType';
import type { CommentType } from '~/model/CommentType';
import type { PaginationType } from '~/model/PaginationType';
import type { ReactionType } from '~/model/ReactionType';
import { VpButton } from '@vtmn-play/react/headless';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Element } from 'react-scroll';
import { Loader } from '@/app/components/Loader';
import { getPaginationNextPage } from '@/app/utils/entities/collection';
import { api } from '~/const/api';
import { isMainUser } from '~/utils/user/user';
import { useAuthenticatedContext } from '../Provider';
import { ItemPost } from './ItemPost';
import { SubItem as Comment } from './SubItem';

export const Item = ({
  forChildren = false,
  entityType = 'answers',
  item,
  focusedSubItem,
  getComments,
  postComment,
  postReaction,
  deleteReaction,
  processing,
  locked = false,
}: {
  forChildren?: boolean;
  focusedSubItem?: number;
  entityType?: CommentEntityType;
  item: AnswerType & CommentType;
  processing: boolean;
  locked?: boolean;
  postReaction: (
    itemId: number,
    subItemId: number,
    reaction: string,
  ) => Promise<ReactionType>;
  deleteReaction: (
    itemId: number,
    subItemId: number,
    reaction: ReactionType,
  ) => Promise<void>;
  getComments: (
    itemUri: string,
    direct_answers_count: number,
    pageUrl?: UriTemplate,
  ) => Promise<PaginationType>;
  postComment: (
    as_admin: boolean,
    value: string,
    parent?: number,
  ) => Promise<void>;
}) => {
  const t = useTranslations('DISCUSSION');

  const { user } = useAuthenticatedContext();

  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(null);

  const isItemPublished
    = item.status === 'published' || item.contribution?.status === 'submitted';

  const matchingFocused = item.comments?.find(c => c.id === focusedSubItem);
  const remainingSubItems = item.comments?.filter(c => c.id !== focusedSubItem);

  const directCount
    = entityType === api.ANSWERS
      ? item.direct_answers_count
      : item.direct_comments_count;

  const canDisplayComments
    = isItemPublished || (!isItemPublished && directCount > 0);

  const openerCount
    = focusedSubItem && matchingFocused ? directCount - 1 : directCount;

  const nextPage = getPaginationNextPage(pagination);

  const handleGetComments = async (
    itemUri: string,
    direct_entity_count: number,
    pageUrl?: UriTemplate,
  ) => {
    setLoading(true);
    const dataPagination = await getComments(
      itemUri,
      direct_entity_count,
      pageUrl,
    );
    setPagination(dataPagination);
    setLoading(false);
  };

  const opener
    // Display opener if comments have not been fetched (undefined)
    // or if there is more comments to fetch
    = canDisplayComments
      && (!item.comments || item.comments.length < directCount)
      && (!locked || (locked && openerCount))
      ? (
          <div className="thread-idea__comments-opener">
            <VpButton
              className="thread-idea__opener"
              data-cy="commentsOpener"
              data-testid="opener"
              variant="tertiary"
              size="small"
              onClick={() => handleGetComments(item['@id'], openerCount)}
              disabled={processing}
            >
              {(openerCount && t(`SEE_N_COMMENT`, {
                count: openerCount,
              }))
              || (isMainUser(user) && t('ANSWER_ACTION'))}
            </VpButton>
          </div>
        )
      : null;

  return (
    <Element name={`c-${item.id}`}>
      <section className="thread-idea" data-testid="item">
        <Loader loading={loading} size="medium">
          <div className="thread-idea__layout">
            <Comment
              subitem={item}
              item={item}
              postReaction={postReaction}
              deleteReaction={deleteReaction}
              processing={processing}
              isItemPublished={isItemPublished}
              locked={locked}
              opener={opener}
            />
            {item.comments && (
              <div
                className="thread-idea__comments-list"
                data-testid="list"
                data-cy="comments"
              >
                {item.comments.length > 0
                  && item.comments.map((comment) => {
                    const isCommentPublished
                      = comment.status === 'published'
                        || comment.contribution?.status === 'submitted';
                    return (
                      <article
                        className="thread-idea__comment"
                        key={`comment${comment.id}`}
                      >
                        <Comment
                          subitem={comment}
                          item={item}
                          postReaction={postReaction}
                          deleteReaction={deleteReaction}
                          processing={processing}
                          isItemPublished={isCommentPublished}
                          locked={locked}
                        />
                      </article>
                    );
                  })}
                {nextPage && (
                  <div className="load-more">
                    <VpButton
                      data-testid="load-more"
                      variant="tertiary"
                      size="small"
                      onClick={() =>
                        handleGetComments(item['@id'], openerCount, nextPage)}
                      disabled={loading}
                    >
                      {t('SEE_MORE_COMMENT')}
                    </VpButton>
                  </div>
                )}
                {
                  // display ItemPost if a comment is on focus
                  // or if comments have been fetched (even if there is no result)
                  ((matchingFocused && remainingSubItems?.length > -1)
                    || (!matchingFocused && remainingSubItems))
                  && !locked && (
                    <ItemPost
                      forChildren={forChildren}
                      id={`post-comment-${item.id}`}
                      postComment={postComment}
                      parentId={item.id}
                      processing={processing}
                    />
                  )
                }
              </div>
            )}
          </div>
        </Loader>
      </section>
    </Element>
  );
};
